import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Container, Grid, Skeleton } from "@mui/material";
import InvestmentGrid from "../../components/Investments/InvestmentGrid";
import OverviewSection from "../../components/Investments/OverviewSection";
import RoiInvestorBenefits from "../../components/Investments/RoiInvestorBenefits";
import RoiInvestorBenefitsNew from "../../components/Investments/RoiInvestorBenefitsNew";
import OperationModelsNew from "../../components/Investments/OperationModelsNew";
import OperationModels from "../../components/Investments/OperationModels";
import OperationalBenefits from "../../components/Investments/OperationalBenefits";
import TrustedPartners from "../../components/Investments/TrustedPartners";
import CommunicationChannel from "../../components/Investments/CommunicationChannel";
import CommercialOperationalBenefits from "../../components/Investments/CommercialOperationalBenefits";
import PickYourCarGrid from "../../components/Investments/PickYourCarGrid";
import InvestmentOpportunity from "../../components/Investments/InvestmentOpportunity";
import LaunchOperatingModel from "../../components/Investments/LaunchOperatingModel";
import Seo from "../../components/common/Seo";
import Header3 from "../../components/header/header-rslhome";
import Footer3 from "../../components/footer/footer-new";
import { InvestmentData } from "../../data/investments";
import { useRouter, useSearchParams } from 'next/navigation';
import InvestmentForm from "../../components/all-services/investmentsForm";
import CtaButton from "../../components/buttonWithArrow";
import InvestmentSimulatorFlow from "../../components/Investments/DiscoverCalculatore";
import { useDispatch, useSelector } from '../../redux/store';
import { getInvestmentEnquiry, getOverAllPriceList, getInvestmentsList } from "../../redux/slices/investments";
import AfterFormSubmit from "../../components/investNow/AfterFormSubmit";
import { fetchSeoByPage } from "../../lib/seoHelper";

const HEADER_OFFSET = 150;

// Optional named anchors (so both #3 and #invest work)
const SECTION_IDS = {
    overviewSection: "overview",
    roiInvestorBenefits: "roi",
    pickYourCarGrid: "pick-your-car",
    investmentOpportunity: "invest",
    roiInvestorBenefitsNew: "roi-new",
    operationModelsNew: "op-models-new",
    operationalBenefits: "operational-benefits",
    trustedPartners: "partners",
    operationModels: "operation-models",
    communicationChannel: "contact",
    launchOperatingModel: "operating-model",
};

// helper to build a DOM id for a section
const domIdFor = (key, sectionId) =>
    sectionId ? sectionId : (SECTION_IDS[key] ?? key);


export default function InvestmentsPage({ meta }) {
    const dispatch = useDispatch();
    const router = useRouter();
    const [selected, setSelected] = useState("");
    const [data, setData] = useState([]);
    const searchParams = useSearchParams()
    const { overAllPriceRes, investEnquiryRes, investmentListRes } = useSelector(
        state => state.investment
    );
    const [formLoader, setFormLoader] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [status, setStatus] = useState(null);
    const [enquiryOpen, setEnquiryOpen] = useState(false)
    const [loader, setLoader] = useState(true);
    const wayParam = searchParams.get('service')
    const [openEnquire, setOpenEnquire] = useState(false);
    const [costCalculateOpen, setCostCalculateOpen] = useState(false);
    const [priceData, setPriceData] = useState([]);
    const [values, setValues] = useState({
        name: "",
        email: "",
        phone: "",
        countryCode: "971",
        itemId: "",
        itemInfo: null,
    });

    useEffect(() => {
        if (wayParam) {
            setSelected(wayParam)
        }
    }, [wayParam])

    // Anchor right above your dynamic sections (below the chooser)
    const sectionsTopRef = useRef(null);

    // Selected dataset
    const current = useMemo(() => InvestmentData[selected], [selected]);

    // Smooth scroll to the start of dynamic sections (with header offset)
    const scrollToSection = () => {
        const el = sectionsTopRef.current;
        if (!el) return;

        const headerOffset = 150; // your header area height
        const rect = el.getBoundingClientRect();
        const scrollTop = window.pageYOffset + rect.top - headerOffset;

        window.scrollTo({ top: scrollTop, behavior: "smooth" });
    }

    // Map each section key to how it renders
    const SECTION_RENDERERS = {
        overviewSection: (cur) =>
            cur?.overviewSection ? (
                <OverviewSection data={cur.overviewSection} onClick={handleOpenEnquire} />
            ) : null,

        roiInvestorBenefits: (cur) =>
            cur?.roiInvestorBenefits ? (
                <RoiInvestorBenefits data={cur.roiInvestorBenefits} onClick={handleOpenEnquire} />
            ) : null,

        operationalBenefits: (cur) =>
            cur?.operationalBenefits ? (
                <OperationalBenefits data={cur.operationalBenefits} onClick={handleOpenEnquire} />
            ) : null,
        trustedPartners: (cur) =>
            cur?.trustedPartners ? (
                <TrustedPartners data={cur.trustedPartners} onClick={handleOpenEnquire} />
            ) : null,

        roiInvestorBenefitsNew: (cur) =>
            cur?.roiInvestorBenefitsNew ? (
                <RoiInvestorBenefitsNew data={cur.roiInvestorBenefitsNew} onClick={handleOpenEnquire} />
            ) : null,

        operationModelsNew: (cur) =>
            cur?.operationModelsNew ? (
                <OperationModelsNew data={cur.operationModelsNew} onClick={handleOpenEnquire} />
            ) : null,

        operationModels: (cur) =>
            cur?.operationModels ? (
                <OperationModels data={cur.operationModels} onClick={handleOpenEnquire} />
            ) : null,

        communicationChannel: (cur) =>
            cur?.communicationChannel ? (
                <CommunicationChannel data={cur.communicationChannel} onClick={handleOpenEnquire} />
            ) : null,

        // This one has no props; show only if truthy flag present
        pickYourCarGrid: (cur) => (cur?.pickYourCarGrid ? <PickYourCarGrid data={cur.pickYourCarGrid} onClick={handleOpenEnquire} /> : null),
        investmentOpportunity: (cur) => (cur?.investmentOpportunity ? <InvestmentOpportunity data={cur.investmentOpportunity} onClick={handleOpenEnquire} /> : null),
        launchOperatingModel: (cur) => (cur?.launchOperatingModel ? <LaunchOperatingModel data={cur.launchOperatingModel} onClick={handleOpenEnquire} /> : null),
        commercialOperationalBenefits: (cur) => (cur?.commercialOperationalBenefits ? <CommercialOperationalBenefits data={cur.commercialOperationalBenefits} onClick={handleOpenEnquire} /> : null),
    };

    // Decide which sections to show and in what order
    const sectionsToShow = useMemo(() => {
        // If the dataset provides an ordered list, use it
        if (Array.isArray(current?.section) && current.section.length > 0) {
            return current.section.filter((key) => key in SECTION_RENDERERS);
        } else {
            if (current?.href) {
                setSelected(null)
                router.push(current?.href);
            }
            return null;
        }
    }, [current]);

    // jump to the top of the sections area
    const scrollToAnchorTop = () => {
        const el = sectionsTopRef.current;
        if (!el) return;
        const top = window.pageYOffset + el.getBoundingClientRect().top - HEADER_OFFSET;
        window.scrollTo({ top, behavior: "smooth" });
    };

    // read the hash and scroll smoothly (supports number, sec-#, or named)
    useEffect(() => {
        const scrollToHash = () => {
            const raw = decodeURIComponent((window.location.hash || "").replace(/^#/, ""));
            if (!raw) return;

            let target = null;

            // 1) direct element id match (works for "overview", "carToInvest", etc.)
            target = document.getElementById(raw);

            // 2) numeric legacy? try data-section-id or sec-<n>
            if (!target && /^\d+$/.test(raw)) {
                target =
                    document.querySelector(`[data-section-id="${raw}"]`) ||
                    document.getElementById(`sec-${raw}`); // legacy support
            }

            // 3) fallback: try derived ids of visible sections
            if (!target) {
                for (const key of sectionsToShow) {
                    const sId = current?.[key]?.sectionId;
                    const id = domIdFor(key, sId);
                    if (id === raw || SECTION_IDS[key] === raw) {
                        target = document.getElementById(id);
                        break;
                    }
                }
            }

            if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
        };

        requestAnimationFrame(scrollToHash);
        window.addEventListener("hashchange", scrollToHash);
        return () => window.removeEventListener("hashchange", scrollToHash);
    }, [sectionsToShow, current]);

    const handleCloseEnquire = () => setOpenEnquire(false);
    const handleOpenEnquire = () => setOpenEnquire(true);

    useEffect(() => {
        dispatch(getOverAllPriceList());
        setLoader(true);
        dispatch(getInvestmentsList());
    }, [dispatch])

    useEffect(() => {
        if (investmentListRes) {
            setLoader(false);
            if (investmentListRes?.status === 1) {
                setData(investmentListRes?.responseData ?? []);
            } else {
                setData([])

            }
        }
    }, [investmentListRes])

    useEffect(() => {
        if (overAllPriceRes) {
            if (overAllPriceRes?.status === 1) {
                setPriceData(overAllPriceRes?.responseData?.priceList ?? []);
            }
            else {
                setPriceData([]);
            }
        }
    }, [overAllPriceRes])

    const handleSubmitInvestNow = (val) => {
        setFormLoader(true);
        dispatch(getInvestmentEnquiry(val))
    }

    const handleClickOpen = () => {
        setEnquiryOpen(true)
    }

    const handleClose = () => {
        handleCloseEnquire();
        setCostCalculateOpen(false);
        setValues({
            name: "",
            email: "",
            phone: "",
            countryCode: "971",
            itemId: "",
            itemInfo: null,
        })
    }

    const onClose = () => {
        if (statusMessage === '' && status === 1) {
            // setOpen(false);
            handleClose();
        }
        setEnquiryOpen(false)
    }

    useEffect(() => {
        if (investEnquiryRes && formLoader) {
            setFormLoader(false)
            if (investEnquiryRes.status === 1) {
                handleClickOpen()
                handleClose();
                setStatusMessage('')
                setStatus(investEnquiryRes.status);
            } else {
                handleClickOpen()
                setStatus(-1)
                setStatusMessage(investEnquiryRes.message || `We’re sorry! Something went wrong. Please try again in a few moments.`)
            }
        }
    }, [investEnquiryRes])

    return (
        <>
            <div style={{ height: "150px", background: "#f8f8f8" }}>
                <Seo meta={meta} />
                <Header3 bgColor="#EBEBEB" borderColor="#DFDFDF" />
            </div>

            {/* Category chooser */}
            <div style={{ background: "#f8f8f8", paddingBottom: "40px" }}>
                <InvestmentGrid
                    data={data}
                    selected={selected}
                    setSelected={setSelected}
                    loader={loader}
                    onChange={(val) => {
                        if ((val !== "LIMOUSINE" && val !== "CAR_RENTAL" && val !== "PRIVATE_JET" && val !== "TRAVEL_TOURISM" && val !== "COMMERCIAL_BROKER" && val !== "ECOMMERCE")) {
                            scrollToSection();
                            // remove query & hash without reloading
                            window.history.replaceState({}, document.title, window.location.pathname);
                        }
                    }}
                />

                <Box mt={4} display="flex" justifyContent="center">
                    <CtaButton label="Discover Where I Can Invest" onClick={() => setCostCalculateOpen(true)} />
                </Box>
            </div>

            <InvestmentSimulatorFlow
                open={costCalculateOpen}
                onClose={() => handleClose()}
                onInvest={(p) => handleSubmitInvestNow(p)}
                setValues={setValues}
                values={values}
                formLoader={formLoader}
                priceData={priceData}
            />

            {/* Anchor (gives natural focus point to scroll to) */}
            <div ref={sectionsTopRef} style={{ scrollMarginTop: HEADER_OFFSET }} />

            {/* render sections with id + data-section-id */}
            {sectionsToShow?.map((key) => {
                const node = SECTION_RENDERERS[key](current);
                if (!node) return null;

                const secMeta = current?.[key];
                const numericId = secMeta?.sectionId && /^\d+$/.test(secMeta.sectionId) ? secMeta.sectionId : undefined;
                const id = domIdFor(key, secMeta?.sectionId); // e.g., "overview", "carToInvest"

                return (
                    <section
                        id={id}
                        data-section-id={numericId ?? undefined}
                        key={id}
                        style={{ scrollMarginTop: HEADER_OFFSET }}
                    >
                        {node}
                    </section>
                );
            })}


            <Footer3 bgcolor="#f8f8f8" />
            <AfterFormSubmit open={enquiryOpen} status={status} message={statusMessage} onClose={onClose} Catalog={statusMessage === ""} />
            <InvestmentForm open={openEnquire} setOpen={setOpenEnquire} onClose={handleCloseEnquire} selected={selected} />
        </>
    );
}

export async function getServerSideProps(context) {
    const rawPath = context.resolvedUrl;
    let path = rawPath.split("?")[0];
    if (path.length > 1 && path.endsWith("/")) {
        path = path.slice(0, -1);
    }
    const metaHome = await fetchSeoByPage(path);
    return {
        props: { meta: metaHome }
    };
}
